/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { defaultValue } from './DateField';
import { analyzeDateField } from './DateFieldAnalyze';

const DateFieldManager: ComponentManagerFn<'DateField'> = (
  platform: Platform,
): ComponentManager<'DateField'> => {
  let callback: () => void = emptyFunction;
  return {
    ...baseComponentManager<'DateField'>(platform, 'DateField'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnBlur',
          'TriggerOnChange',
          'TriggerOnFocus',
          'PulseTip',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    isPropertyVisible: (property, itemData, { appUid, pageId, itemId }) => {
      switch (property) {
        case 'showTime': {
          const store = platform.getParentStore(appUid, pageId, itemId);
          const attr = store?.getAttribute(itemData.viewAttribute);
          return (
            attr?.attributeType === 'DateTime' ||
            attr?.attributeType === 'StringDateTime'
          );
        }
        default:
          return true;
      }
    },
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: ({ props }: RequiredItem<'DateColumn'>) => {
          if (props.showTime)
            return [
              'DateTime',
              'StringDateTime',
              'ServerDate',
              'ServerDateTime',
            ];
          return [
            'Date',
            'DateTime',
            'StringDateTime',
            'ServerDate',
            'ServerDateTime',
          ];
        },
        attributeRefreshCallback: (cb: () => void) => {
          callback = cb;
        },
      },
      minDateAttribute: {
        label: 'Min Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: ({ props }: RequiredItem<'DateColumn'>) => {
          if (props.showTime) return ['DateTime', 'StringDateTime'];
          return ['Date', 'DateTime', 'StringDateTime'];
        },
      },
      maxDateAttribute: {
        label: 'Max Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: ({ props }: RequiredItem<'DateColumn'>) => {
          if (props.showTime) return ['DateTime', 'StringDateTime'];
          return ['Date', 'DateTime', 'StringDateTime'];
        },
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      showTime: {
        label: 'Show Time',
        type: 'boolean',
      },
      hideTodayButton: {
        label: 'Hide Today Button',
        type: 'boolean',
      },
      disablePast: {
        label: 'Disable Past',
        type: 'boolean',
        helpText: 'Enable this to disable the past days',
      },
      format: {
        label: 'Format',
        type: 'string',
        helpText: 'e.g. MM/dd/yyyy hh:mm a',
      },
      onChangeFunction: {
        label: 'On Change',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onKeyDownFunction: {
        label: 'On Key Down',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onFocusFunction: {
        label: 'On Focus',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onBlurFunction: {
        label: 'On Blur',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      disableDateFunction: {
        label: 'Disable Date Function',
        type: 'controllerFunction',
        category: 'Controllers',
        helpText:
          'Provide a controller function to check which dates should be disabled. It should return a boolean value.',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'showTime') {
        callback();
      }
      return {
        [property]: value,
      };
    },
    getDisplayText: (item) => item.props.viewAttribute ?? '',
    analyze: analyzeDateField,
    name: 'Date Field',
    category: 'Forms',
    icon: { iconName: 'calendar-edit' },
    replaceableWith: ['NumberField', 'TextField'],
    onReplace: (item) => {
      delete item.props.viewAttribute;
      return item;
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    description: `**Overview**<br/>
The Date Field component is a versatile form control that allows users to select a date and optional time. It provides an interactive calendar interface (date picker) and supports manual input with format validation.
Use this component for any form input requiring temporal data, from simple birthdays to complex scheduling systems. It supports validation, dynamic range restrictions, and custom formatting.

**1. Data Binding**<br/>
This component binds to a Date or DateTime column in your data store.
* **View Attribute:** Select the Data Store field to read from and write to.
* **Supported Types:** Date, DateTime, StringDateTime, ServerDate, ServerDateTime.
* **Note:** If **Show Time** is enabled, the underlying attribute must support time (e.g., DateTime or StringDateTime). You cannot bind a pure Date column if time selection is active.

**2. Configuration & Formatting**<br/>
You can customize how the date appears and how users interact with the picker.
* **Show Time:** A boolean toggle. If enabled, the picker allows users to select hours and minutes alongside the date.
* **Format:** A string pattern defining how the date is displayed in the input box.
    * **Examples:** MM/dd/yyyy, dd-MMM-yyyy, MM/dd/yyyy hh:mm a.
    * **Special Use Case:** Use "MM/yyyy" for Credit Card expiration dates.
* **Placeholder:** Text displayed when the field is empty.
* **Hide Today Button:** Hides the shortcut button that jumps the calendar to the current date.

**3. Validation & Constraints**<br/>
The Date Field offers multiple layers of validation to ensure data integrity.
* **Disable Past:** A simple boolean switch to prevent users from selecting any date prior to "today."
* **Dynamic Ranges (Min/Max):**
    * **Min Date Attribute:** Bind this to another field (e.g., StartDate) to ensure the user cannot select a date earlier than that value.
    * **Max Date Attribute:** Bind this to a field to set an upper limit on the selectable range.
* **Advanced Logic (Controller):**
    * **Disable Date Function:** A controller function returning a boolean. Use this to programmatically disable specific days, such as weekends or holidays.

**4. Events & Triggers**<br/>
The component supports standard form events and server-side validation hooks.
* **Triggers:** You can attach child logic components like **TriggerOnChange**, **TriggerOnBlur**, and **TriggerOnFocus**.
* **Server Validate On Change:** If set to true, the system will immediately send the value to the server for validation logic as soon as the user changes the date, rather than waiting for the form submit.

**5. Best Use Cases**<br/>
* **Scheduling & Appointments:** Using **Show Time** to book specific slots.
* **Booking Systems:** Using **Min Date Attribute** on a "Check-out" field to ensure it is always after the "Check-in" date.
* **Financial Forms:** Using "MM/yyyy" formatting for credit card expiration input.
* **Task Management:** Setting **Disable Past** to true for task due dates to ensure tasks aren't scheduled in the past.`,
    helpRenders: [
      {
        title: 'Default Date Field',
        description: 'With Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        visibleProps: [],
        item: {
          itemId: 'tf',
          props: {
            viewAttribute: 'someDateTime',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'DateField',
          parent: 'form',
        },
      },
      {
        title: 'Date Field w/ Label',
        description: 'With Label',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['label'],
        item: {
          itemId: 'tf1',
          props: {
            label: { type: 'text', value: 'Start Date' },
            viewAttribute: 'someDate',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'DateField',
          parent: 'form',
        },
      },
      {
        title: 'With Time',
        description:
          "The view attribute must of type 'Date Time' for the 'Show Time' prop to show up.",
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['showTime'],
        item: {
          itemId: 'tf1',
          props: {
            label: { type: 'text', value: 'Start Date' },
            viewAttribute: 'someDateTime',
            flex: {
              all: 12,
            },
            showTime: true,
          },
          children: [],
          type: 'DateField',
          parent: 'form',
        },
      },
      {
        title: 'Date Format',
        description: 'Custom Date Format e.g. Credit Card Expiry',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['format'],
        item: {
          itemId: 'tf1',
          props: {
            label: { type: 'text', value: 'Start Date' },
            viewAttribute: 'someDateTime',
            flex: {
              all: 12,
            },
            format: 'MM/yyyy',
          },
          children: [],
          type: 'DateField',
          parent: 'form',
        },
      },
      {
        title: 'Readonly',
        description:
          'Turning off the insertable prop will disable the control while entering a new row. Updatable prop will disable editing an existing row.',
        page: SamplePage,
        exampleId: 'e6',
        height: 260,
        visibleProps: ['insertable'],
        item: {
          itemId: 'tf4',
          props: {
            label: { type: 'text', value: 'Start Date' },
            viewAttribute: 'someDate',
            flex: {
              all: 12,
            },
            insertable: { type: 'boolean', value: false },
          },
          children: [],
          type: 'DateField',
          parent: 'form',
        },
      },
    ],
  };
};

export default DateFieldManager;
